// src/Settings.js
import React, { useState, useEffect } from "react";
import SeedTeams from "./SeedTeams";
import { supabase } from "./supabaseClient";
import toast from "react-hot-toast";

const cardStyle = {
  background: "#ffffff",
  padding: "1.25rem",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  marginBottom: "1.5rem",
};

const inputStyle = {
  padding: "10px 12px",
  borderRadius: "6px",
  border: "1px solid #d1d5db",
  fontSize: "0.95rem",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: "#fff",
};

const labelStyle = {
  display: "block",
  marginBottom: "0.4rem",
  fontWeight: "600",
  fontSize: "0.875rem",
  color: "#374151",
};

const buttonStyle = {
  padding: "10px 20px",
  backgroundColor: "#2563eb",
  color: "white",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
  fontSize: "0.95rem",
};

const Settings = ({ teams = [], addGameResult }) => {
  const [winnerId, setWinnerId] = useState("");
  const [loserId, setLoserId] = useState("");
  const [winnerScore, setWinnerScore] = useState("");
  const [loserScore, setLoserScore] = useState("");
  const [strengthOverride, setStrengthOverride] = useState("");
  const [marketOpen, setMarketOpen] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real-time listener for market status in Supabase
  useEffect(() => {
    // 1. Fetch initial status
    const fetchMarketStatus = async () => {
      try {
        const { data, error } = await supabase
          .from("config")
          .select("is_open")
          .eq("id", "market")
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setMarketOpen(data.is_open ?? true);
        }
      } catch (error) {
        console.error("Error fetching market status:", error);
      }
    };

    fetchMarketStatus();

    // 2. Subscribe to real-time changes
    const channel = supabase
      .channel("market-status-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "config",
          filter: "id=eq.market",
        },
        (payload) => {
          if (payload.new && payload.new.is_open !== undefined) {
            setMarketOpen(payload.new.is_open);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const toggleMarket = async () => {
    setIsToggling(true);
    const newStatus = !marketOpen;
    try {
      const { error } = await supabase
        .from("config")
        .upsert({ id: "market", is_open: newStatus });

      if (error) throw error;

      toast.success(`Market is now ${newStatus ? "OPEN" : "CLOSED"}`);
    } catch (error) {
      toast.error(`Failed to update market: ${error.message}`);
    } finally {
      setIsToggling(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (teams.length === 0) {
      return toast.error("No teams available in the system.");
    }
    if (!winnerId || !loserId) {
      return toast.error("Please select both a winning and losing team.");
    }
    if (winnerId === loserId) {
      return toast.error("Winner and loser cannot be the same team.");
    }

    const wScoreNum = winnerScore ? Number(winnerScore) : 0;
    const lScoreNum = loserScore ? Number(loserScore) : 0;

    if (winnerScore !== "" && loserScore !== "" && wScoreNum <= lScoreNum) {
      return toast.error("Winner's score must be higher than loser's score.");
    }

    setIsSubmitting(true);
    try {
      if (addGameResult) {
        await addGameResult(
          winnerId,
          loserId,
          wScoreNum,
          lScoreNum,
          strengthOverride !== "" ? Number(strengthOverride) : null
        );
      }
      toast.success("Game result recorded successfully!");
      setWinnerId("");
      setLoserId("");
      setWinnerScore("");
      setLoserScore("");
      setStrengthOverride("");
    } catch (error) {
      toast.error(`Failed to record result: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasTeams = teams && teams.length > 0;

  return (
    <div style={{ maxWidth: "850px", margin: "0 auto", padding: "1.5rem" }}>
      <h2 style={{ marginBottom: "1.25rem", color: "#111827" }}>
        Admin Control Center
      </h2>

      {/* 1. Market Status Control */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: "0.5rem", color: "#111827" }}>
          Market Status Control
        </h3>
        <p style={{ margin: "0 0 1rem 0", color: "#4b5563", fontSize: "0.95rem" }}>
          Current Status:{" "}
          <strong style={{ color: marketOpen ? "#16a34a" : "#dc2626" }}>
            {marketOpen ? "OPEN (Trading Active)" : "CLOSED (Game Day Lock)"}
          </strong>
        </p>
        <button
          onClick={toggleMarket}
          disabled={isToggling}
          style={{
            ...buttonStyle,
            backgroundColor: marketOpen ? "#dc2626" : "#16a34a",
            opacity: isToggling ? 0.7 : 1,
            cursor: isToggling ? "not-allowed" : "pointer",
          }}
        >
          {isToggling
            ? "Updating..."
            : marketOpen
            ? "Close Market (Game Day Lock)"
            : "Open Market"}
        </button>
      </div>

      {/* 2. Record Game Result */}
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1rem",
          }}
        >
          <h3 style={{ margin: 0, color: "#111827" }}>Record Game Result</h3>
          <span
            style={{
              fontSize: "0.85rem",
              color: hasTeams ? "#16a34a" : "#dc2626",
              fontWeight: "600",
            }}
          >
            {hasTeams ? `✓ ${teams.length} teams loaded` : "⚠️ 0 teams loaded"}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Team Dropdowns */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            <div>
              <label style={labelStyle}>Winner Team</label>
              <select
                value={winnerId}
                onChange={(e) => setWinnerId(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: !hasTeams ? "#fca5a5" : "#d1d5db",
                  backgroundColor: !hasTeams ? "#fef2f2" : "#ffffff",
                }}
                disabled={!hasTeams}
                required
              >
                {!hasTeams ? (
                  <option value="">No teams available (Seed database below)</option>
                ) : (
                  <>
                    <option value="">Select Winner...</option>
                    {teams.map((t) => {
                      const key = t.id || t.name;
                      return (
                        <option key={key} value={key}>
                          {t.name || key}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Loser Team</label>
              <select
                value={loserId}
                onChange={(e) => setLoserId(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: !hasTeams ? "#fca5a5" : "#d1d5db",
                  backgroundColor: !hasTeams ? "#fef2f2" : "#ffffff",
                }}
                disabled={!hasTeams}
                required
              >
                {!hasTeams ? (
                  <option value="">No teams available (Seed database below)</option>
                ) : (
                  <>
                    <option value="">Select Loser...</option>
                    {teams.map((t) => {
                      const key = t.id || t.name;
                      return (
                        <option key={key} value={key}>
                          {t.name || key}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
            </div>
          </div>

          {/* Score Inputs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "1rem",
              marginBottom: "1.25rem",
            }}
          >
            <div>
              <label style={labelStyle}>Winner Score</label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 35"
                value={winnerScore}
                onChange={(e) => setWinnerScore(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Loser Score</label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 21"
                value={loserScore}
                onChange={(e) => setLoserScore(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Strength Override (Opt.)</label>
              <input
                type="number"
                placeholder="e.g. 85"
                value={strengthOverride}
                onChange={(e) => setStrengthOverride(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !hasTeams}
            style={{
              ...buttonStyle,
              opacity: isSubmitting || !hasTeams ? 0.6 : 1,
              cursor: isSubmitting || !hasTeams ? "not-allowed" : "pointer",
            }}
          >
            {isSubmitting ? "Submitting Result..." : "Submit Game Result"}
          </button>
        </form>
      </div>

      {/* 3. Database Seeding Tool */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: "0.75rem", color: "#111827" }}>
          Database Seeding
        </h3>
        <SeedTeams />
      </div>
    </div>
  );
};

export default Settings;